// ===================================================================
// VolunteerOS -- Volunteer Roster
// Search, filter, CRUD, CSV import/export, detail modal with tabs
// ===================================================================

import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  ActionIcon, Modal, Stack, Tabs, FileButton, Pagination,
  Checkbox, SimpleGrid, ScrollArea,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconDownload,
  IconUpload, IconUser, IconPhone, IconCertificate, IconNotes,
  IconRefresh, IconCalendar, IconShieldCheck,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import Papa from 'papaparse';
import { formatPhone } from '../../lib/phoneFormat';
import {
  useVolunteerStore,
  type Volunteer,
  parseExtras,
} from '../../store/volunteerStore';

// ── constants ────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'probationary', label: 'Probationary' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'dnd', label: 'Do Not Deploy' },
];

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'active', label: 'Active / Field' },
  { value: 'office', label: 'Office' },
  { value: 'medical', label: 'Medical' },
  { value: 'comms', label: 'Communications' },
  { value: 'both', label: 'Both' },
];

const VEHICLE_OPTIONS = [
  { value: 'all', label: 'Any Vehicle' },
  { value: 'yes', label: 'Has Vehicle' },
  { value: 'no', label: 'No Vehicle' },
];

const ROLE_PREF_SELECT = [
  { value: 'active', label: 'Field' },
  { value: 'office', label: 'Admin' },
  { value: 'medical', label: 'Medical' },
  { value: 'comms', label: 'Comms' },
  { value: 'both', label: 'Flexible' },
];

const SHIRT_SIZES = [
  { value: '', label: '-- Select --' },
  { value: 'XS', label: 'XS' },
  { value: 'S', label: 'S' },
  { value: 'M', label: 'M' },
  { value: 'L', label: 'L' },
  { value: 'XL', label: 'XL' },
  { value: '2XL', label: '2XL' },
  { value: '3XL', label: '3XL' },
];

const VEHICLE_SELECT = [
  { value: '', label: 'None' },
  { value: 'personal', label: 'Personal' },
  { value: 'transport', label: 'Can Transport Others' },
];

const CERT_OPTIONS = [
  'CPR/AED', 'First Aid', 'CERT', 'ICS 100', 'ICS 200', 'ICS 300',
  'ICS 700', 'Ham Radio', 'Chainsaw', 'Swift Water', 'Wilderness FA',
];

const SKILL_OPTIONS = [
  'Medical', 'Communications', 'Logistics', 'Search & Rescue',
  'Heavy Equipment', 'Counseling', 'Translation', 'Cooking',
  'Construction', 'IT/Tech',
];

const EQUIPMENT_OPTIONS = [
  'Chainsaw', 'Generator', 'Water Pump', 'ATV', 'Boat',
  'Trailer', 'Ham Radio', 'Medical Bag',
];

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const DAY_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};
const PERIODS = ['morning', 'afternoon', 'evening', 'overnight'] as const;
const PERIOD_LABELS: Record<string, string> = {
  morning: 'AM', afternoon: 'PM', evening: 'Eve', overnight: 'Night',
};

const WAIVER_TEXT = `VOLUNTEER LIABILITY WAIVER AND RELEASE OF LIABILITY

I, the undersigned volunteer, hereby acknowledge and agree to the following:

1. ASSUMPTION OF RISK: I understand that volunteering for emergency management activities involves inherent risks, including but not limited to: physical injury, exposure to hazardous materials or conditions, emotional distress, property damage, and other dangers associated with disaster response and recovery operations.

2. RELEASE OF LIABILITY: I hereby release, waive, discharge, and covenant not to sue the organization, its officers, directors, employees, agents, and representatives from any and all liability, claims, demands, actions, or causes of action arising out of or related to any loss, damage, or injury that may be sustained by me during my volunteer activities.

3. INDEMNIFICATION: I agree to indemnify and hold harmless the organization from any loss, liability, damage, or cost it may incur due to my participation in volunteer activities.

4. MEDICAL AUTHORIZATION: In the event of an emergency, I authorize the organization to secure emergency medical treatment on my behalf. I understand that the organization does not provide medical insurance for volunteers.

5. PHOTO/MEDIA RELEASE: I grant permission to the organization to use my image, likeness, and voice in photographs, video, and other media for promotional and educational purposes.

6. CODE OF CONDUCT: I agree to follow all safety protocols, instructions from supervisors, and the organization's code of conduct while serving as a volunteer.

7. CONFIDENTIALITY: I agree to maintain the confidentiality of any sensitive information I may encounter during my volunteer service.

This waiver is binding upon me, my heirs, executors, administrators, and assigns. I have read this document carefully and understand its contents. I sign it voluntarily and with full knowledge of its significance.`;

const PAGE_SIZE = 20;

function statusColor(s: string): string {
  switch (s) {
    case 'active': return 'green';
    case 'probationary': return 'yellow';
    case 'inactive': return 'gray';
    case 'dnd': return 'red';
    default: return 'gray';
  }
}

// Helper: toggle item in comma-separated string
function toggleCsv(csv: string, item: string): string {
  const arr = csv.split(',').map((s) => s.trim()).filter(Boolean);
  const idx = arr.indexOf(item);
  if (idx >= 0) arr.splice(idx, 1);
  else arr.push(item);
  return arr.join(', ');
}

function csvIncludes(csv: string, item: string): boolean {
  return csv.split(',').map((s) => s.trim()).includes(item);
}

// ── component ────────────────────────────────────────────────────

export default function Roster() {
  const {
    volunteers, loadVolunteers,
    createVolunteer, updateVolunteer, deleteVolunteer,
  } = useVolunteerStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [roleFilter, setRoleFilter] = useState('all');
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [page, setPage] = useState(1);

  // modals
  const [detailVol, setDetailVol] = useState<Volunteer | null>(null);
  const [editVol, setEditVol] = useState<Volunteer | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Volunteer | null>(null);
  const [saving, setSaving] = useState(false);
  const [formTab, setFormTab] = useState<string | null>('contact');

  // form fields — Tab 1: Contact
  const [formFirstName, setFormFirstName] = useState('');
  const [formLastName, setFormLastName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formOrg, setFormOrg] = useState('');
  const [formStatus, setFormStatus] = useState<string | null>('active');
  const [formDob, setFormDob] = useState('');
  const [formAddress, setFormAddress] = useState('');
  const [formCity, setFormCity] = useState('');
  const [formState, setFormState] = useState('');
  const [formZip, setFormZip] = useState('');
  const [formShirtSize, setFormShirtSize] = useState<string | null>('');
  const [formDietary, setFormDietary] = useState('');

  // form fields — Tab 2: Emergency Contact
  const [formEcName, setFormEcName] = useState('');
  const [formEcRelationship, setFormEcRelationship] = useState('');
  const [formEcPhone, setFormEcPhone] = useState('');
  const [formEcEmail, setFormEcEmail] = useState('');

  // form fields — Tab 3: Skills & Availability
  const [formCerts, setFormCerts] = useState('');
  const [formSkills, setFormSkills] = useState('');
  const [formEquipment, setFormEquipment] = useState('');
  const [formLanguages, setFormLanguages] = useState('');
  const [formRolePref, setFormRolePref] = useState<string | null>('active');
  const [formVehicle, setFormVehicle] = useState<string | null>('');

  // form fields — Tab 4: Availability grid
  const [formAvailability, setFormAvailability] = useState<Record<string, Record<string, boolean>>>({});

  // form fields — Tab 5: Waiver
  const [formWaiverAccepted, setFormWaiverAccepted] = useState(false);
  const [formWaiverDate, setFormWaiverDate] = useState('');

  const [_formNotes, setFormNotes] = useState('');

  const initialLoad = useRef(false);

  useEffect(() => {
    if (initialLoad.current) return;
    initialLoad.current = true;
    loadVolunteers();
  }, [loadVolunteers]);

  // ── filtering / sorting ────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = volunteers;
    if (statusFilter !== 'all') list = list.filter((v) => v.status === statusFilter);
    if (roleFilter !== 'all') list = list.filter((v) => v.rolePref === roleFilter);
    if (vehicleFilter !== 'all') {
      list = list.filter((v) => {
        const extras = parseExtras(v.notes);
        const hasVehicle = !!extras.vehicle;
        return vehicleFilter === 'yes' ? hasVehicle : !hasVehicle;
      });
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((v) =>
        `${v.firstName || ''} ${v.lastName || ''}`.toLowerCase().includes(q) ||
        (v.email || '').toLowerCase().includes(q) ||
        (v.phone || '').includes(q) ||
        (v.org || '').toLowerCase().includes(q) ||
        (v.certs || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) =>
      `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`),
    );
  }, [volunteers, statusFilter, roleFilter, vehicleFilter, search]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [statusFilter, roleFilter, vehicleFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── form helpers ───────────────────────────────────────────────
  const resetForm = useCallback(() => {
    setFormFirstName('');
    setFormLastName('');
    setFormEmail('');
    setFormPhone('');
    setFormOrg('');
    setFormStatus('active');
    setFormDob('');
    setFormAddress('');
    setFormCity('');
    setFormState('');
    setFormZip('');
    setFormShirtSize('');
    setFormDietary('');
    setFormEcName('');
    setFormEcRelationship('');
    setFormEcPhone('');
    setFormEcEmail('');
    setFormCerts('');
    setFormSkills('');
    setFormEquipment('');
    setFormLanguages('');
    setFormRolePref('active');
    setFormVehicle('');
    setFormAvailability({});
    setFormWaiverAccepted(false);
    setFormWaiverDate('');
    setFormNotes('');
    setFormTab('contact');
  }, []);

  const populateForm = useCallback((v: Volunteer) => {
    const extras = parseExtras(v.notes);
    setFormFirstName(v.firstName);
    setFormLastName(v.lastName);
    setFormEmail(v.email);
    setFormPhone(v.phone);
    setFormOrg(v.org);
    setFormStatus(v.status || 'active');
    setFormDob((extras.dob as string) || '');
    setFormAddress((extras.address as string) || '');
    setFormCity((extras.city as string) || '');
    setFormState((extras.state as string) || '');
    setFormZip((extras.zip as string) || '');
    setFormShirtSize((extras.shirtSize as string) || '');
    setFormDietary((extras.dietary as string) || '');
    setFormEcName(v.ecName);
    setFormEcRelationship((extras.ecRelationship as string) || '');
    setFormEcPhone(v.ecPhone);
    setFormEcEmail((extras.ecEmail as string) || '');
    setFormCerts(v.certs);
    setFormSkills(v.skills);
    setFormEquipment((extras.equipment as string) || '');
    setFormLanguages((extras.languages as string) || '');
    setFormRolePref(v.rolePref || 'active');
    setFormVehicle((extras.vehicle as string) || '');
    setFormWaiverAccepted(!!(extras.waiverAccepted));
    setFormWaiverDate((extras.waiverDate as string) || '');
    setFormNotes(v.notes);
    setFormTab('contact');
    // parse availability
    try {
      const avail = JSON.parse(v.availability || '{}');
      setFormAvailability(typeof avail === 'object' && avail !== null ? avail : {});
    } catch {
      setFormAvailability({});
    }
  }, []);

  const openCreate = () => { resetForm(); setShowCreate(true); };
  const openEdit = (v: Volunteer) => { populateForm(v); setEditVol(v); };

  const toggleAvailability = (day: string, period: string) => {
    setFormAvailability((prev) => {
      const daySlots = { ...(prev[day] || {}) };
      daySlots[period] = !daySlots[period];
      return { ...prev, [day]: daySlots };
    });
  };

  const handleSave = async () => {
    if (!formFirstName.trim() || !formLastName.trim()) {
      notifications.show({ message: 'First and last name are required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      // Build extras object for notes JSON
      const extras: Record<string, unknown> = {};
      if (formDob) extras.dob = formDob;
      if (formAddress) extras.address = formAddress;
      if (formCity) extras.city = formCity;
      if (formState) extras.state = formState;
      if (formZip) extras.zip = formZip;
      if (formShirtSize) extras.shirtSize = formShirtSize;
      if (formDietary) extras.dietary = formDietary;
      if (formEcRelationship) extras.ecRelationship = formEcRelationship;
      if (formEcEmail) extras.ecEmail = formEcEmail;
      if (formVehicle) extras.vehicle = formVehicle;
      if (formEquipment) extras.equipment = formEquipment;
      if (formLanguages) extras.languages = formLanguages;
      if (formWaiverAccepted) {
        extras.waiverAccepted = true;
        extras.waiverDate = formWaiverDate || new Date().toISOString();
      }

      // Preserve any extra keys that were in the original notes but not managed by this form
      const existingExtras = editVol ? parseExtras(editVol.notes) : {};
      const managedKeys = [
        'dob', 'address', 'city', 'state', 'zip', 'shirtSize', 'dietary',
        'ecRelationship', 'ecEmail', 'vehicle', 'equipment', 'languages',
        'waiverAccepted', 'waiverDate',
      ];
      for (const [k, v] of Object.entries(existingExtras)) {
        if (!managedKeys.includes(k) && !(k in extras)) {
          extras[k] = v;
        }
      }

      const notesJson = Object.keys(extras).length > 0 ? JSON.stringify(extras) : '';

      const payload = {
        firstName: formFirstName.trim(),
        lastName: formLastName.trim(),
        email: formEmail.trim(),
        phone: formPhone.trim(),
        org: formOrg.trim(),
        rolePref: formRolePref || 'active',
        status: formStatus || 'active',
        certs: formCerts.trim(),
        skills: formSkills.trim(),
        availability: JSON.stringify(formAvailability),
        ecName: formEcName.trim(),
        ecPhone: formEcPhone.trim(),
        notes: notesJson,
        registeredAt: editVol?.registeredAt || new Date().toISOString(),
      };
      if (editVol) {
        await updateVolunteer(editVol.id, payload);
        notifications.show({ message: 'Volunteer updated', color: 'green' });
        setEditVol(null);
      } else {
        await createVolunteer(payload);
        notifications.show({ message: 'Volunteer created', color: 'green' });
        setShowCreate(false);
      }
      resetForm();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      notifications.show({ message: msg, color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteVolunteer(deleteTarget.id);
      notifications.show({
        message: `${deleteTarget.firstName} ${deleteTarget.lastName} removed`,
        color: 'gray',
      });
      setDeleteTarget(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      notifications.show({ message: msg, color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  // ── CSV import ─────────────────────────────────────────────────
  const handleImport = (file: File | null) => {
    if (!file) return;
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        let imported = 0;
        const errors: string[] = [];
        for (let i = 0; i < results.data.length; i++) {
          const row = results.data[i];
          const firstName = (row.firstName || row.first_name || '').trim();
          const lastName = (row.lastName || row.last_name || '').trim();
          if (!firstName && !lastName) { errors.push(`Row ${i + 2}: missing name`); continue; }
          try {
            await createVolunteer({
              firstName,
              lastName,
              email: (row.email || '').trim(),
              phone: (row.phone || '').trim(),
              org: (row.org || row.organization || '').trim(),
              rolePref: (row.role || row.rolePref || 'active').trim(),
              status: (row.status || 'active').trim(),
              certs: (row.certs || '').trim(),
              skills: (row.skills || '').trim(),
              availability: '{}',
              ecName: '',
              ecPhone: '',
              notes: '',
              registeredAt: new Date().toISOString(),
            });
            imported++;
          } catch (err) {
            errors.push(`Row ${i + 2} (${firstName} ${lastName}): ${err instanceof Error ? err.message : 'failed'}`);
          }
        }
        if (errors.length > 0) {
          notifications.show({
            title: `Imported ${imported}, ${errors.length} failed`,
            message: errors.slice(0, 5).join('\n') + (errors.length > 5 ? `\n...and ${errors.length - 5} more` : ''),
            color: 'orange',
            autoClose: false,
          });
        } else {
          notifications.show({ message: `Imported ${imported} volunteer(s)`, color: 'green' });
        }
      },
    });
  };

  // ── CSV export ─────────────────────────────────────────────────
  const handleExport = () => {
    const rows = filtered.map((v) => ({
      firstName: v.firstName,
      lastName: v.lastName,
      email: v.email,
      phone: v.phone,
      org: v.org,
      rolePref: v.rolePref,
      status: v.status,
      certs: v.certs,
      skills: v.skills,
    }));
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `volunteers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── quick status change ────────────────────────────────────────
  const handleQuickStatus = async (v: Volunteer, newStatus: string) => {
    try {
      await updateVolunteer(v.id, { status: newStatus });
      notifications.show({
        message: `${v.firstName} ${v.lastName} set to ${newStatus}`,
        color: statusColor(newStatus),
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Update failed';
      notifications.show({ message: msg, color: 'red' });
    }
  };

  // ── tabbed form modal body (shared by create & edit) ───────────
  const formBody = (
    <Stack gap={0}>
      <Tabs value={formTab} onChange={setFormTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="contact" leftSection={<IconUser size={14} />}>Contact</Tabs.Tab>
          <Tabs.Tab value="emergency" leftSection={<IconPhone size={14} />}>Emergency</Tabs.Tab>
          <Tabs.Tab value="skills" leftSection={<IconCertificate size={14} />}>Skills</Tabs.Tab>
          <Tabs.Tab value="availability" leftSection={<IconCalendar size={14} />}>Availability</Tabs.Tab>
          <Tabs.Tab value="waiver" leftSection={<IconShieldCheck size={14} />}>Waiver</Tabs.Tab>
        </Tabs.List>

        {/* Tab 1: Contact */}
        <Tabs.Panel value="contact">
          <Stack gap="sm">
            <Group grow>
              <TextInput label="First Name" required value={formFirstName}
                onChange={(e) => setFormFirstName(e.currentTarget.value)} size="sm" />
              <TextInput label="Last Name" required value={formLastName}
                onChange={(e) => setFormLastName(e.currentTarget.value)} size="sm" />
            </Group>
            <Group grow>
              <TextInput label="Email" value={formEmail}
                onChange={(e) => setFormEmail(e.currentTarget.value)} size="sm" />
              <TextInput label="Phone" value={formPhone}
                onChange={(e) => setFormPhone(e.currentTarget.value)} size="sm" />
            </Group>
            <TextInput label="Organization" value={formOrg}
              onChange={(e) => setFormOrg(e.currentTarget.value)} size="sm" />
            <Group grow>
              <TextInput label="Date of Birth" type="date" value={formDob}
                onChange={(e) => setFormDob(e.currentTarget.value)} size="sm" />
              <Select label="Status" data={STATUS_OPTIONS.filter((s) => s.value !== 'all')}
                value={formStatus} onChange={setFormStatus} size="sm" />
            </Group>
            <TextInput label="Address" value={formAddress}
              onChange={(e) => setFormAddress(e.currentTarget.value)} size="sm" />
            <Group grow>
              <TextInput label="City" value={formCity}
                onChange={(e) => setFormCity(e.currentTarget.value)} size="sm" />
              <TextInput label="State" value={formState}
                onChange={(e) => setFormState(e.currentTarget.value)} size="sm" style={{ maxWidth: 100 }} />
              <TextInput label="ZIP" value={formZip}
                onChange={(e) => setFormZip(e.currentTarget.value)} size="sm" style={{ maxWidth: 120 }} />
            </Group>
            <Group grow>
              <Select label="T-Shirt Size" data={SHIRT_SIZES}
                value={formShirtSize} onChange={setFormShirtSize} size="sm" />
              <TextInput label="Dietary Restrictions / Allergies" value={formDietary}
                onChange={(e) => setFormDietary(e.currentTarget.value)} size="sm" />
            </Group>
          </Stack>
        </Tabs.Panel>

        {/* Tab 2: Emergency Contact */}
        <Tabs.Panel value="emergency">
          <Stack gap="sm">
            <TextInput label="Emergency Contact Name" value={formEcName}
              onChange={(e) => setFormEcName(e.currentTarget.value)} size="sm" />
            <TextInput label="Relationship" value={formEcRelationship}
              onChange={(e) => setFormEcRelationship(e.currentTarget.value)} size="sm" />
            <Group grow>
              <TextInput label="Emergency Phone" value={formEcPhone}
                onChange={(e) => setFormEcPhone(e.currentTarget.value)} size="sm" />
              <TextInput label="Emergency Email" value={formEcEmail}
                onChange={(e) => setFormEcEmail(e.currentTarget.value)} size="sm" />
            </Group>
          </Stack>
        </Tabs.Panel>

        {/* Tab 3: Skills */}
        <Tabs.Panel value="skills">
          <Stack gap="md">
            <div>
              <Text size="sm" fw={600} mb={6}>Certifications</Text>
              <Group gap="xs" wrap="wrap">
                {CERT_OPTIONS.map((cert) => (
                  <Checkbox key={cert} label={cert} size="xs"
                    checked={csvIncludes(formCerts, cert)}
                    onChange={() => setFormCerts(toggleCsv(formCerts, cert))} />
                ))}
              </Group>
            </div>
            <div>
              <Text size="sm" fw={600} mb={6}>Skills</Text>
              <Group gap="xs" wrap="wrap">
                {SKILL_OPTIONS.map((skill) => (
                  <Checkbox key={skill} label={skill} size="xs"
                    checked={csvIncludes(formSkills, skill)}
                    onChange={() => setFormSkills(toggleCsv(formSkills, skill))} />
                ))}
              </Group>
            </div>
            <div>
              <Text size="sm" fw={600} mb={6}>Equipment Available</Text>
              <Group gap="xs" wrap="wrap">
                {EQUIPMENT_OPTIONS.map((eq) => (
                  <Checkbox key={eq} label={eq} size="xs"
                    checked={csvIncludes(formEquipment, eq)}
                    onChange={() => setFormEquipment(toggleCsv(formEquipment, eq))} />
                ))}
              </Group>
            </div>
            <TextInput label="Languages Spoken (comma-separated)" value={formLanguages}
              onChange={(e) => setFormLanguages(e.currentTarget.value)} size="sm" />
            <Group grow>
              <Select label="Role Preference" data={ROLE_PREF_SELECT}
                value={formRolePref} onChange={setFormRolePref} size="sm" />
              <Select label="Vehicle" data={VEHICLE_SELECT}
                value={formVehicle} onChange={setFormVehicle} size="sm" />
            </Group>
          </Stack>
        </Tabs.Panel>

        {/* Tab 4: Availability Grid */}
        <Tabs.Panel value="availability">
          <Stack gap="sm">
            <Text size="sm" c="dimmed">Click cells to toggle availability</Text>
            <SimpleGrid cols={8} spacing={4}>
              {/* Header row */}
              <Paper p={4} radius="sm" />
              {DAYS.map((day) => (
                <Paper key={day} p={4} radius="sm" bg="var(--mantine-color-gray-1)" ta="center">
                  <Text size="xs" fw={700}>{DAY_LABELS[day]}</Text>
                </Paper>
              ))}
              {/* Period rows */}
              {PERIODS.map((period) => (
                <>
                  <Paper key={`label-${period}`} p={4} radius="sm" bg="var(--mantine-color-gray-1)">
                    <Text size="xs" fw={600} ta="center">{PERIOD_LABELS[period]}</Text>
                  </Paper>
                  {DAYS.map((day) => {
                    const active = !!(formAvailability[day]?.[period]);
                    return (
                      <Paper
                        key={`${day}-${period}`}
                        p={4}
                        radius="sm"
                        withBorder
                        ta="center"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: active ? 'var(--mantine-color-green-1)' : undefined,
                          borderColor: active ? 'var(--mantine-color-green-5)' : undefined,
                        }}
                        onClick={() => toggleAvailability(day, period)}
                      >
                        <Text size="xs" fw={active ? 700 : 400} c={active ? 'green' : 'dimmed'}>
                          {active ? '\u2713' : '\u2014'}
                        </Text>
                      </Paper>
                    );
                  })}
                </>
              ))}
            </SimpleGrid>
          </Stack>
        </Tabs.Panel>

        {/* Tab 5: Waiver */}
        <Tabs.Panel value="waiver">
          <Stack gap="sm">
            <ScrollArea h={250} type="auto" offsetScrollbars>
              <Paper p="md" radius="sm" withBorder>
                <Text size="xs" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {WAIVER_TEXT}
                </Text>
              </Paper>
            </ScrollArea>
            <Checkbox
              label="I have read and agree to the Volunteer Liability Waiver & Release"
              checked={formWaiverAccepted}
              onChange={(e) => {
                setFormWaiverAccepted(e.currentTarget.checked);
                if (e.currentTarget.checked && !formWaiverDate) {
                  setFormWaiverDate(new Date().toISOString());
                }
              }}
              size="sm"
            />
            {formWaiverDate && (
              <Text size="xs" c="dimmed">
                Waiver accepted: {new Date(formWaiverDate).toLocaleDateString()}
              </Text>
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>

      <Group justify="flex-end" mt="md">
        <Button variant="light" onClick={() => { setShowCreate(false); setEditVol(null); resetForm(); }}>
          Cancel
        </Button>
        <Button onClick={handleSave} loading={saving}>
          {editVol ? 'Update' : 'Create'}
        </Button>
      </Group>
    </Stack>
  );

  // ── detail modal ───────────────────────────────────────────────
  const detailExtras = detailVol ? parseExtras(detailVol.notes) : {};

  // ── render ─────────────────────────────────────────────────────
  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Volunteer Roster</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            onClick={() => loadVolunteers()}>
            Refresh
          </Button>
          <FileButton onChange={handleImport} accept=".csv">
            {(props) => (
              <Button size="xs" variant="light" leftSection={<IconUpload size={14} />} {...props}>
                Import CSV
              </Button>
            )}
          </FileButton>
          <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
            onClick={handleExport} disabled={filtered.length === 0}>
            Export CSV
          </Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            Add Volunteer
          </Button>
        </Group>
      </Group>

      {/* Filters */}
      <Group mb="md" gap="sm">
        <TextInput
          placeholder="Search name, email, phone, org, certs..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          size="xs"
          style={{ flex: 1, minWidth: 200 }}
        />
        <Select data={STATUS_OPTIONS} value={statusFilter}
          onChange={(v) => setStatusFilter(v ?? 'all')} size="xs" w={150} />
        <Select data={ROLE_OPTIONS} value={roleFilter}
          onChange={(v) => setRoleFilter(v ?? 'all')} size="xs" w={150} />
        <Select data={VEHICLE_OPTIONS} value={vehicleFilter}
          onChange={(v) => setVehicleFilter(v ?? 'all')} size="xs" w={140} />
      </Group>

      <Text size="xs" c="dimmed" mb="sm">
        Showing {paged.length} of {filtered.length} volunteer(s)
      </Text>

      {/* Table */}
      {paged.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No volunteers found.</Text>
        </Paper>
      ) : (
        <Paper p={0} radius="sm" withBorder>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--frost)' }}>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Email</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Phone</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Org</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Role</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Certs</th>
                  <th style={{ padding: '10px 12px' }} />
                </tr>
              </thead>
              <tbody>
                {paged.map((v) => (
                  <tr key={v.id} style={{ borderBottom: '1px solid var(--frost)', cursor: 'pointer' }}
                    onClick={() => setDetailVol(v)}>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                      {v.firstName} {v.lastName}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{v.email || '--'}</td>
                    <td style={{ padding: '8px 12px' }}>{v.phone || '--'}</td>
                    <td style={{ padding: '8px 12px' }}>{v.org || '--'}</td>
                    <td style={{ padding: '8px 12px', textTransform: 'capitalize' }}>
                      {v.rolePref || '--'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Select
                        data={STATUS_OPTIONS.filter((s) => s.value !== 'all')}
                        value={v.status}
                        onChange={(val) => { if (val) handleQuickStatus(v, val); }}
                        size="xs"
                        w={120}
                        onClick={(e) => e.stopPropagation()}
                        styles={{ input: { fontWeight: 600, fontSize: 11 } }}
                      />
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Group gap={4} wrap="wrap">
                        {v.certs
                          ? v.certs.split(',').filter(Boolean).map((c) => (
                              <Badge key={c.trim()} size="xs" variant="light">
                                {c.trim()}
                              </Badge>
                            ))
                          : <Text size="xs" c="dimmed">--</Text>}
                      </Group>
                    </td>
                    <td style={{ padding: '8px 12px' }} onClick={(e) => e.stopPropagation()}>
                      <Group gap={4}>
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(v)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                        <ActionIcon size="sm" variant="subtle" color="red"
                          onClick={() => setDeleteTarget(v)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Paper>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
        </Group>
      )}

      {/* Create Modal */}
      <Modal opened={showCreate} onClose={() => { setShowCreate(false); resetForm(); }}
        title="Add Volunteer" size="lg" centered>
        {formBody}
      </Modal>

      {/* Edit Modal */}
      <Modal opened={!!editVol} onClose={() => { setEditVol(null); resetForm(); }}
        title="Edit Volunteer" size="lg" centered>
        {formBody}
      </Modal>

      {/* Detail Modal */}
      <Modal opened={!!detailVol} onClose={() => setDetailVol(null)}
        title={detailVol ? `${detailVol.firstName} ${detailVol.lastName}` : ''}
        size="lg" centered>
        {detailVol && (
          <Tabs defaultValue="contact">
            <Tabs.List mb="md">
              <Tabs.Tab value="contact" leftSection={<IconUser size={14} />}>Contact</Tabs.Tab>
              <Tabs.Tab value="emergency" leftSection={<IconPhone size={14} />}>Emergency</Tabs.Tab>
              <Tabs.Tab value="skills" leftSection={<IconCertificate size={14} />}>Skills</Tabs.Tab>
              <Tabs.Tab value="notes" leftSection={<IconNotes size={14} />}>Notes</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="contact">
              <Stack gap="xs">
                <Group gap="lg">
                  <div>
                    <Text size="xs" c="dimmed">Name</Text>
                    <Text size="sm" fw={600}>{detailVol.firstName} {detailVol.lastName}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Organization</Text>
                    <Text size="sm">{detailVol.org || '--'}</Text>
                  </div>
                </Group>
                <Group gap="lg">
                  <div>
                    <Text size="xs" c="dimmed">Email</Text>
                    <Text size="sm">{detailVol.email || '--'}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Phone</Text>
                    <Text size="sm">{detailVol.phone ? formatPhone(detailVol.phone) : '--'}</Text>
                  </div>
                </Group>
                <div>
                  <Text size="xs" c="dimmed">Role Preference</Text>
                  <Badge size="sm" variant="light" style={{ textTransform: 'capitalize' }}>
                    {detailVol.rolePref || 'active'}
                  </Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Status</Text>
                  <Badge size="sm" color={statusColor(detailVol.status)}>
                    {detailVol.status}
                  </Badge>
                </div>
                {!!(detailExtras.dob || detailExtras.address || detailExtras.shirtSize || detailExtras.dietary) && (
                  <>
                    {detailExtras.dob && (
                      <div>
                        <Text size="xs" c="dimmed">Date of Birth</Text>
                        <Text size="sm">{String(detailExtras.dob)}</Text>
                      </div>
                    )}
                    {(detailExtras.address || detailExtras.city) && (
                      <div>
                        <Text size="xs" c="dimmed">Address</Text>
                        <Text size="sm">
                          {[detailExtras.address, detailExtras.city, detailExtras.state, detailExtras.zip]
                            .filter(Boolean).map(String).join(', ')}
                        </Text>
                      </div>
                    )}
                    {detailExtras.shirtSize && (
                      <div>
                        <Text size="xs" c="dimmed">T-Shirt Size</Text>
                        <Text size="sm">{String(detailExtras.shirtSize)}</Text>
                      </div>
                    )}
                    {detailExtras.dietary && (
                      <div>
                        <Text size="xs" c="dimmed">Dietary Restrictions</Text>
                        <Text size="sm">{String(detailExtras.dietary)}</Text>
                      </div>
                    )}
                  </>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="emergency">
              <Stack gap="xs">
                <div>
                  <Text size="xs" c="dimmed">Emergency Contact Name</Text>
                  <Text size="sm" fw={600}>{detailVol.ecName || '--'}</Text>
                </div>
                {!!detailExtras.ecRelationship && (
                  <div>
                    <Text size="xs" c="dimmed">Relationship</Text>
                    <Text size="sm">{String(detailExtras.ecRelationship)}</Text>
                  </div>
                )}
                <div>
                  <Text size="xs" c="dimmed">Emergency Contact Phone</Text>
                  <Text size="sm">{detailVol.ecPhone || '--'}</Text>
                </div>
                {!!detailExtras.ecEmail && (
                  <div>
                    <Text size="xs" c="dimmed">Emergency Contact Email</Text>
                    <Text size="sm">{String(detailExtras.ecEmail)}</Text>
                  </div>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="skills">
              <Stack gap="sm">
                <div>
                  <Text size="xs" c="dimmed" mb={4}>Certifications</Text>
                  <Group gap={4} wrap="wrap">
                    {detailVol.certs
                      ? detailVol.certs.split(',').filter(Boolean).map((c) => (
                          <Badge key={c.trim()} size="sm" variant="light" color="blue">
                            {c.trim()}
                          </Badge>
                        ))
                      : <Text size="sm" c="dimmed">None</Text>}
                  </Group>
                </div>
                <div>
                  <Text size="xs" c="dimmed" mb={4}>Skills</Text>
                  <Group gap={4} wrap="wrap">
                    {detailVol.skills
                      ? detailVol.skills.split(',').filter(Boolean).map((s) => (
                          <Badge key={s.trim()} size="sm" variant="light" color="grape">
                            {s.trim()}
                          </Badge>
                        ))
                      : <Text size="sm" c="dimmed">None</Text>}
                  </Group>
                </div>
                {!!detailExtras.equipment && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Equipment</Text>
                    <Group gap={4} wrap="wrap">
                      {String(detailExtras.equipment).split(',').filter(Boolean).map((eq) => (
                        <Badge key={eq.trim()} size="sm" variant="light" color="orange">
                          {eq.trim()}
                        </Badge>
                      ))}
                    </Group>
                  </div>
                )}
                {!!detailExtras.languages && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Languages</Text>
                    <Text size="sm">{String(detailExtras.languages)}</Text>
                  </div>
                )}
                {!!detailExtras.vehicle && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Vehicle</Text>
                    <Badge size="sm" variant="light" color="cyan" style={{ textTransform: 'capitalize' }}>
                      {String(detailExtras.vehicle)}
                    </Badge>
                  </div>
                )}
                <div>
                  <Text size="xs" c="dimmed" mb={4}>Availability</Text>
                  {detailVol.availability && detailVol.availability !== '{}'
                    ? (() => {
                        try {
                          const avail = JSON.parse(detailVol.availability) as Record<string, Record<string, boolean>>;
                          const days = Object.keys(avail);
                          if (days.length === 0) return <Text size="sm" c="dimmed">Not set</Text>;
                          return (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, fontSize: 11 }}>
                              {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((day) => {
                                const slots = avail[day] || {};
                                const active = Object.entries(slots).filter(([, v]) => v).map(([k]) => k);
                                return (
                                  <Paper key={day} p={4} radius="sm" withBorder ta="center">
                                    <Text size="xs" fw={700} tt="uppercase">{day}</Text>
                                    {active.length > 0
                                      ? active.map((slot) => (
                                          <Badge key={slot} size="xs" variant="dot" color="green" mt={2}>
                                            {slot}
                                          </Badge>
                                        ))
                                      : <Text size="xs" c="dimmed">--</Text>}
                                  </Paper>
                                );
                              })}
                            </div>
                          );
                        } catch {
                          return <Text size="sm" c="dimmed">Invalid data</Text>;
                        }
                      })()
                    : <Text size="sm" c="dimmed">Not set</Text>}
                </div>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="notes">
              <Stack gap="xs">
                {!!detailExtras.waiverAccepted && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Waiver Status</Text>
                    <Group gap="xs">
                      <Badge size="sm" color="green" variant="light">Waiver Signed</Badge>
                      {!!detailExtras.waiverDate && (
                        <Text size="xs" c="dimmed">
                          on {new Date(String(detailExtras.waiverDate)).toLocaleDateString()}
                        </Text>
                      )}
                    </Group>
                  </div>
                )}
                {detailVol.notes && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Notes / Extra Fields</Text>
                    {Object.keys(detailExtras).length > 0
                      ? (
                          <Paper p="sm" radius="sm" withBorder>
                            {Object.entries(detailExtras).map(([key, val]) => (
                              <Group key={key} gap="xs" mb={2}>
                                <Text size="xs" fw={600} style={{ textTransform: 'capitalize' }}>
                                  {key}:
                                </Text>
                                <Text size="xs">{String(val)}</Text>
                              </Group>
                            ))}
                          </Paper>
                        )
                      : <Text size="sm">{detailVol.notes}</Text>}
                  </div>
                )}
                {!detailVol.notes && !detailExtras.waiverAccepted && (
                  <Text size="sm" c="dimmed">No notes.</Text>
                )}
              </Stack>
            </Tabs.Panel>
          </Tabs>
        )}
        {detailVol && (
          <Group justify="flex-end" mt="md">
            <Button size="xs" variant="light" leftSection={<IconPencil size={14} />}
              onClick={() => { setDetailVol(null); openEdit(detailVol); }}>
              Edit
            </Button>
          </Group>
        )}
      </Modal>

      {/* Delete Confirm */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        title="Remove Volunteer" centered size="sm">
        <Text size="sm" mb="md">
          Remove {deleteTarget?.firstName} {deleteTarget?.lastName} from the roster?
          This action cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="light" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="red" onClick={handleDelete} loading={saving}>Remove</Button>
        </Group>
      </Modal>
    </div>
  );
}
